import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { Model } from 'app/model/backendmodel';
import { BlobTypeStoreReadSettings } from 'app/model/pipeline/copysource/store-read-settings/blob-type-store-read-settings';
import { genericStoreReadSettingsAdapts, StoreReadSettingsAdapter } from './store-read-settings.adapter';
import { CopySource } from 'app/model/pipeline/copysource/copysource';
import _ from 'lodash';
import { FilePathMode } from 'app/registries/consts/metadata-consts';

export const blobTypeStoreReadSettingsAdapts = {
    ...genericStoreReadSettingsAdapts,
    enablePartitionDiscovery: '*',
    partitionRootPath: '*',
    modifiedDatetimeStart: '*',
    modifiedDatetimeEnd: '*',
    recursive: '*',
    maxConcurrentConnections: '*',
    wildcardFolderPath: '*',
    wildcardFileName: '*',
    fileListPath: '*'
};

export abstract class BlobTypeStoreReadSettingsAdapter<
    T extends BlobTypeStoreReadSettings,
    U extends Model.StoreReadSettings
> extends StoreReadSettingsAdapter<T, U> {
    protected override async _toResource(model: T): Promise<U> {
        const resource = await super._toResource(model);
        if (model != null && resource != null) {
            if (model.filePathMode !== FilePathMode.LIST) {
                resource.recursive = ValueAdapter.toResource(model, BlobTypeStoreReadSettings.RECURSIVE_SETTING);
                resource.modifiedDatetimeStart = ValueAdapter.toResource(
                    model,
                    BlobTypeStoreReadSettings.MODIFID_DATETIME_START
                );
                resource.modifiedDatetimeEnd = ValueAdapter.toResource(
                    model,
                    BlobTypeStoreReadSettings.MODIFID_DATETIME_END
                );
            }
            resource.maxConcurrentConnections = ValueAdapter.toResource(
                model,
                BlobTypeStoreReadSettings.MAXCONCURRENT_CONNECTIONS
            );
            if (model.filePathMode === FilePathMode.WILDCARD) {
                resource.wildcardFolderPath =
                    ValueAdapter.toResource(model, BlobTypeStoreReadSettings.WILDCARD_FOLDERPATH) || undefined;
                resource.wildcardFileName =
                    ValueAdapter.toResource(model, BlobTypeStoreReadSettings.WILDCARD_FILENAME) || undefined;
            }
            if (model.filePathMode === FilePathMode.LIST) {
                resource.fileListPath =
                    ValueAdapter.toResource(model, BlobTypeStoreReadSettings.FILE_LIST_PATH) || undefined;
                resource.wildcardFileName = resource.wildcardFolderPath = undefined;
            }
            resource.enablePartitionDiscovery = ValueAdapter.toResource(
                model,
                BlobTypeStoreReadSettings.ENABLE_PARTITION_DISCOVERY
            );
            if (resource.enablePartitionDiscovery) {
                resource.partitionRootPath =
                    ValueAdapter.toResource(model, BlobTypeStoreReadSettings.PARTITION_ROOT_PATH) || undefined;
            }
        }

        return resource as U;
    }

    protected override async _toModel(resource: U, copySource: CopySource): Promise<T> {
        const model = await super._toModel(resource, copySource);
        if (model != null && resource != null) {
            ValueAdapter.toModel(model, BlobTypeStoreReadSettings.RECURSIVE_SETTING, resource.recursive);
            ValueAdapter.toModel(
                model,
                BlobTypeStoreReadSettings.MAXCONCURRENT_CONNECTIONS,
                resource.maxConcurrentConnections
            );
            if (resource.wildcardFolderPath || resource.wildcardFileName) {
                model.filePathMode = FilePathMode.WILDCARD;
                ValueAdapter.toModel(model, BlobTypeStoreReadSettings.WILDCARD_FOLDERPATH, resource.wildcardFolderPath);
                ValueAdapter.toModel(model, BlobTypeStoreReadSettings.WILDCARD_FILENAME, resource.wildcardFileName);
            } else if (resource.fileListPath) {
                model.filePathMode = FilePathMode.LIST;
                ValueAdapter.toModel(model, BlobTypeStoreReadSettings.FILE_LIST_PATH, resource.fileListPath);
            } else {
                model.filePathMode = FilePathMode.DATASET;
            }
            model.enablePartitionDiscovery = undefined;
            ValueAdapter.toModel(
                model,
                BlobTypeStoreReadSettings.ENABLE_PARTITION_DISCOVERY,
                resource.enablePartitionDiscovery
            );
            if (resource.enablePartitionDiscovery) {
                ValueAdapter.toModel(model, BlobTypeStoreReadSettings.PARTITION_ROOT_PATH, resource.partitionRootPath);
            }
            ValueAdapter.toModel(
                model,
                BlobTypeStoreReadSettings.MODIFID_DATETIME_START,
                resource.modifiedDatetimeStart
            );
            ValueAdapter.toModel(model, BlobTypeStoreReadSettings.MODIFID_DATETIME_END, resource.modifiedDatetimeEnd);
        }

        return model;
    }
}
